import React, { useState, useEffect, useRef } from 'react';
import { Card } from '../components/ui';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOBreadcrumbs from '../components/SEOBreadcrumbs';
import { useTranslation } from '../hooks/useTranslation';
import { usePageSEO } from '../hooks/usePageSEO';
import { ReactComponent as AIIcon } from '../assets/icons/ai.svg';
import { 
  Restaurant, 
  FitnessCenter, 
  TrendingUp, 
  WaterDrop, 
  Flag,
  Favorite,
  LocalPharmacy,
  CalendarMonth,
  Notifications,
  Warning,
  Schedule,
  Apple,
  Android,
  Psychology,
  Groups,
  HealthAndSafety,
  MenuBook,
  Insights,
  AutoGraph,
  LocalDining,
  FoodBank,
  Egg,
  Fastfood,
  Cookie,
  LunchDining,
  EmojiEvents,
  GetApp,
  ExpandMore,
  ExpandLess
} from '../mui-icons';
import iconImage from '../assets/icon.webp';
import './AboutPage.css';

const AboutPage = () => {
  const navigate = useNavigate();
  const { t, translateHTML } = useTranslation();
  const [expandedSection, setExpandedSection] = useState(null);
  const [activeSection, setActiveSection] = useState('vision');
  const sectionRefs = useRef({});
  
  // SEO dinâmico para esta página
  usePageSEO();

  // Seções do menu lateral (apenas as 5 primeiras)
  const sections = [
    { id: 'vision', icon: <Groups />, color: '#4CAF50' },
    { id: 'features', icon: <HealthAndSafety />, color: '#2196F3' },
    { id: 'nutrition', icon: <Restaurant />, color: '#FF9800' },
    { id: 'howItWorks', icon: <AutoGraph />, color: '#9C27B0' },
    { id: 'technology', icon: <Insights />, color: '#00BCD4' }
  ];

  // Features data
  const features = [
    {
      icon: <AIIcon style={{ width: 40, height: 40, fill: 'white' }} />,
      title: t('about.featuresList.ai.title'),
      description: t('about.featuresList.ai.description'),
      details: t('about.featuresList.ai.details'),
      color: 'linear-gradient(135deg, #2196F3, #21CBF3)'
    },
    {
      icon: <Restaurant sx={{ fontSize: 40 }} />,
      title: t('about.featuresList.mealPlanning.title'),
      description: t('about.featuresList.mealPlanning.description'),
      details: t('about.featuresList.mealPlanning.details'),
      color: 'linear-gradient(135deg, #FF6B6B, #FF8E53)'
    },
    {
      icon: <TrendingUp sx={{ fontSize: 40 }} />,
      title: t('about.featuresList.monitoring.title'),
      description: t('about.featuresList.monitoring.description'),
      details: t('about.featuresList.monitoring.details'),
      color: 'linear-gradient(135deg, #4CAF50, #8BC34A)'
    },
    {
      icon: <WaterDrop sx={{ fontSize: 40 }} />,
      title: t('about.featuresList.hydration.title'),
      description: t('about.featuresList.hydration.description'),
      details: t('about.featuresList.hydration.details'),
      color: 'linear-gradient(135deg, #03A9F4, #00BCD4)'
    },
    {
      icon: <FitnessCenter sx={{ fontSize: 40 }} />,
      title: t('about.featuresList.activities.title'),
      description: t('about.featuresList.activities.description'),
      details: t('about.featuresList.activities.details'),
      color: 'linear-gradient(135deg, #9C27B0, #BA68C8)'
    },
    {
      icon: <Flag sx={{ fontSize: 40 }} />,
      title: t('about.featuresList.goals.title'),
      description: t('about.featuresList.goals.description'),
      details: t('about.featuresList.goals.details'),
      color: 'linear-gradient(135deg, #FF9800, #FFB74D)'
    },
    {
      icon: <CalendarMonth sx={{ fontSize: 40 }} />,
      title: t('about.featuresList.cycle.title'),
      description: t('about.featuresList.cycle.description'),
      details: t('about.featuresList.cycle.details'),
      color: 'linear-gradient(135deg, #E91E63, #F06292)'
    },
    {
      icon: <LocalPharmacy sx={{ fontSize: 40 }} />,
      title: t('about.featuresList.medication.title'),
      description: t('about.featuresList.medication.description'),
      details: t('about.featuresList.medication.details'),
      color: 'linear-gradient(135deg, #3F51B5, #5C6BC0)'
    },
    {
      icon: <Warning sx={{ fontSize: 40 }} />,
      title: t('about.featuresList.allergies.title'),
      description: t('about.featuresList.allergies.description'),
      details: t('about.featuresList.allergies.details'),
      color: 'linear-gradient(135deg, #FF5722, #FF7043)'
    },
    {
      icon: <Schedule sx={{ fontSize: 40 }} />,
      title: t('about.featuresList.routines.title'),
      description: t('about.featuresList.routines.description'),
      details: t('about.featuresList.routines.details'),
      color: 'linear-gradient(135deg, #607D8B, #90A4AE)'
    },
    {
      icon: <Notifications sx={{ fontSize: 40 }} />,
      title: t('about.featuresList.notifications.title'),
      description: t('about.featuresList.notifications.description'),
      details: t('about.featuresList.notifications.details'),
      color: 'linear-gradient(135deg, #795548, #A1887F)'
    },
    {
      icon: <MenuBook sx={{ fontSize: 40 }} />,
      title: t('about.featuresList.recipes.title'),
      description: t('about.featuresList.recipes.description'),
      details: t('about.featuresList.recipes.details'),
      color: 'linear-gradient(135deg, #00897B, #26A69A)'
    }
  ];

  const nutritionFeatures = [
    {
      icon: <LocalDining sx={{ fontSize: 32 }} />,
      title: t('about.nutritionList.macronutrients.title'),
      description: t('about.nutritionList.macronutrients.description')
    },
    {
      icon: <FoodBank sx={{ fontSize: 32 }} />,
      title: t('about.nutritionList.micronutrients.title'),
      description: t('about.nutritionList.micronutrients.description')
    },
    {
      icon: <Egg sx={{ fontSize: 32 }} />,
      title: t('about.nutritionList.calories.title'),
      description: t('about.nutritionList.calories.description')
    },
    {
      icon: <Fastfood sx={{ fontSize: 32 }} />,
      title: t('about.nutritionList.glycemic.title'),
      description: t('about.nutritionList.glycemic.description')
    },
    {
      icon: <Cookie sx={{ fontSize: 32 }} />,
      title: t('about.nutritionList.fibers.title'),
      description: t('about.nutritionList.fibers.description')
    },
    {
      icon: <LunchDining sx={{ fontSize: 32 }} />,
      title: t('about.nutritionList.balanced.title'),
      description: t('about.nutritionList.balanced.description')
    }
  ];

  const stats = [
    { number: '10.000+', label: t('about.stats.recipes') },
    { number: '50+', label: t('about.stats.features') },
    { number: '100%', label: t('about.stats.personalized') },
    { number: '24/7', label: t('about.stats.aiAvailable') }
  ];

  // Scroll to section and expand
  const scrollToSection = (sectionId) => {
    const element = sectionRefs.current[sectionId];
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setExpandedSection(sectionId);
      setActiveSection(sectionId);
    }
  };

  // Toggle section expansion
  const toggleSection = (sectionId) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  // Intersection Observer
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-100px 0px -80% 0px',
      threshold: 0
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.dataset.section);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  // Render Vision section
  const renderVisionSection = () => (
    <div className="section-content">
      <div className="vision-content-main">
        <Groups sx={{ fontSize: 60, color: 'var(--primary-green)' }} />
        <p className="vision-text-main">{t('about.vision.text')}</p>
      </div>
    </div>
  );

  // Render Features section
  const renderFeaturesSection = () => (
    <div className="section-content">
      <p className="section-subtitle-text">{t('about.features.subtitle')}</p>
      <div className="features-grid-about">
        {features.map((feature, index) => (
          <div key={index} className="feature-card pn-card pn-card--marketing-mini">
            <div className="feature-card pn-card pn-card--marketing-header-mini" style={{ background: feature.color }}>
              <div className="feature-icon-mini">
                {feature.icon}
              </div>
              <h3 className="feature-title-mini">{feature.title}</h3>
            </div>
            <div className="feature-card pn-card pn-card--marketing-body-mini">
              <p className="feature-description-mini">{feature.description}</p>
              <ul className="feature-details-list-mini">
                {feature.details.map((detail, idx) => (
                  <li key={idx}>{detail}</li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Render Nutrition section
  const renderNutritionSection = () => (
    <div className="section-content">
      <p className="section-subtitle-text">{t('about.nutrition.subtitle')}</p>
      <div className="nutrition-grid-about">
        {nutritionFeatures.map((item, index) => (
          <div key={index} className="nutrition-card-about">
            <div className="nutrition-icon-about">
              {item.icon}
            </div>
            <h4 className="nutrition-title-about">{item.title}</h4>
            <p className="nutrition-description-about">{item.description}</p>
          </div>
        ))}
      </div>
    </div>
  );

  // Render How It Works section
  const renderHowItWorksSection = () => (
    <div className="section-content">
      <div className="steps-grid-about">
        <div className="step-card-about">
          <div className="step-number">1</div>
          <h3 className="step-title-about">{t('about.steps.step1.title')}</h3>
          <p className="step-description-about">{t('about.steps.step1.description')}</p>
        </div>
        <div className="step-card-about">
          <div className="step-number">2</div>
          <h3 className="step-title-about">{t('about.steps.step2.title')}</h3>
          <p className="step-description-about">{t('about.steps.step2.description')}</p>
        </div>
        <div className="step-card-about">
          <div className="step-number">3</div>
          <h3 className="step-title-about">{t('about.steps.step3.title')}</h3>
          <p className="step-description-about">{t('about.steps.step3.description')}</p>
        </div>
        <div className="step-card-about">
          <div className="step-number">4</div>
          <h3 className="step-title-about">{t('about.steps.step4.title')}</h3>
          <p className="step-description-about">{t('about.steps.step4.description')}</p>
        </div>
      </div>
    </div>
  );

  // Render Technology section
  const renderTechnologySection = () => (
    <div className="section-content">
      <div className="technology-content-main">
        <Insights sx={{ fontSize: 60, color: 'var(--primary-green)' }} />
        <p className="technology-text-main">{t('about.technology.text')}</p>
        <div className="technology-badges-main">
          <div className="tech-badge-main">
            <AutoGraph sx={{ fontSize: 24 }} />
            <span>{t('about.technology.badges.ml')}</span>
          </div>
          <div className="tech-badge-main">
            <Psychology sx={{ fontSize: 24 }} />
            <span>{t('about.technology.badges.dl')}</span>
          </div>
          <div className="tech-badge-main">
            <Insights sx={{ fontSize: 24 }} />
            <span>{t('about.technology.badges.predictive')}</span>
          </div>
        </div>
      </div>
    </div>
  );

  // Render Stats section (não expansível)
  const renderStatsSection = () => (
    <div className="stats-section-main">
      <div className="stats-grid-main">
        {stats.map((stat, index) => (
          <div key={index} className="stat-card pn-card pn-card--stat-main">
            <h3 className="stat-number-main">{stat.number}</h3>
            <p className="stat-label-main">{stat.label}</p>
          </div>
        ))}
      </div>
    </div>
  );

  // Render Download section (não expansível)
  const renderDownloadSection = () => (
    <div className="cta-section-main">
      <h2 className="cta-title-main">{t('about.cta.title')}</h2>
      <p className="cta-subtitle-main">{t('about.cta.subtitle')}</p>
      <div className="cta-buttons-main">
        <a 
          href="https://apps.apple.com/br/app/personalnutri" 
          target="_blank" 
          rel="noopener noreferrer" 
          className="store-button-main apple"
        >
          <Apple sx={{ fontSize: 32 }} />
          <div className="store-button-text">
            <span className="store-button-small">{t('about.download.apple.small')}</span>
            <span className="store-button-large">{t('about.download.apple.large')}</span>
          </div>
        </a>
        <a 
          href="https://play.google.com/store/apps/details?id=com.personalnutri.app&pcampaignid=web_share" 
          target="_blank" 
          rel="noopener noreferrer" 
          className="store-button-main google"
        >
          <Android sx={{ fontSize: 32 }} />
          <div className="store-button-text">
            <span className="store-button-small">{t('about.download.google.small')}</span>
            <span className="store-button-large">{t('about.download.google.large')}</span>
          </div>
        </a>
      </div>
    </div>
  );

  // Section renderers mapping
  const sectionRenderers = {
    vision: renderVisionSection,
    features: renderFeaturesSection,
    nutrition: renderNutritionSection,
    howItWorks: renderHowItWorksSection,
    technology: renderTechnologySection,
    stats: renderStatsSection,
    download: renderDownloadSection
  };

  return (
    <div className="about-page surface-marketing">
      <Header />
      
      {/* SEO Breadcrumbs */}
      <SEOBreadcrumbs />

      {/* Hero Section */}
      <section className="about-hero">
        <div className="container">
          <div className="about-hero-content">
            <div className="about-hero-text">
              <h1 className="about-hero-title" dangerouslySetInnerHTML={translateHTML('about.hero.title')} />
              <p className="about-hero-subtitle">{t('about.hero.subtitle')}</p>
              <div className="about-hero-badges">
                <div className="hero-badge">
                  <AIIcon style={{ width: 24, height: 24, fill: 'white' }} />
                  <span>{t('about.hero.badges.ai')}</span>
                </div>
                <div className="hero-badge">
                  <Psychology sx={{ fontSize: 24 }} />
                  <span>{t('about.hero.badges.personalized')}</span>
                </div>
                <div className="hero-badge">
                  <HealthAndSafety sx={{ fontSize: 24 }} />
                  <span>{t('about.hero.badges.science')}</span>
                </div>
              </div>
            </div>
            <div className="about-hero-image">
              <div className="about-hero-icon">
                <img src={iconImage} alt="PersonalNutri" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content with Sidebar */}
      <div className="about-layout">
        <div className="container about-container">
          {/* Sidebar Navigation */}
          <aside className="about-sidebar">
            <nav className="sidebar-nav">
              {sections.map((section) => (
                <button
                  key={section.id}
                  className={`sidebar-item ${activeSection === section.id ? 'active' : ''}`}
                  onClick={() => scrollToSection(section.id)}
                  style={{ '--section-color': section.color }}
                >
                  <div className="sidebar-icon" style={{ color: section.color }}>
                    {section.icon}
                  </div>
                  <span className="sidebar-label">{t(`about.${section.id}.title`)}</span>
                </button>
              ))}
            </nav>
          </aside>

          {/* Content Area */}
          <main className="about-content">
            {/* Seções Expansíveis */}
            {sections.map((section) => (
              <div
                key={section.id}
                ref={(el) => (sectionRefs.current[section.id] = el)}
                data-section={section.id}
                className="section-wrapper"
              >
                <div
                  className={`section-card ${expandedSection === section.id ? 'expanded' : ''}`}
                  onClick={() => toggleSection(section.id)}
                >
                  <div className="section-header">
                    <div className="section-header-left">
                      <div className="section-icon" style={{ backgroundColor: section.color }}>
                        {section.icon}
                      </div>
                      <h2 className="section-title">{t(`about.${section.id}.title`)}</h2>
                    </div>
                    <div className="section-toggle">
                      {expandedSection === section.id ? (
                        <ExpandLess sx={{ fontSize: 32 }} />
                      ) : (
                        <ExpandMore sx={{ fontSize: 32 }} />
                      )}
                    </div>
                  </div>

                  {expandedSection === section.id && (
                    <div className="section-body" onClick={(e) => e.stopPropagation()}>
                      {sectionRenderers[section.id]()}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Seções Estáticas no Final (sem menu lateral) */}
            <div className="section-wrapper">
              <div className="section-card pn-card pn-card--marketing pn-card--marketing-expandable-static">
                {renderStatsSection()}
              </div>
            </div>

            <div className="section-wrapper">
              <div className="section-card pn-card pn-card--marketing pn-card--marketing-expandable-static">
                {renderDownloadSection()}
              </div>
            </div>
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AboutPage;
